import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [footerContent, setFooterContent] = useState({
    company_name: 'VISAASURE',
    description: 'Your trusted partner for visa consultation and immigration services. Helping individuals and families achieve their global dreams since 2009.',
    copyright_text: '© 2024 Visaasure. All rights reserved. | Mumbai, India'
  });
  const [quickLinks, setQuickLinks] = useState({
    services: { label: 'Services', enabled: true, url: '#services' },
    top_destinations: { label: 'Top Destinations', enabled: true, url: '#get-visa' },
    process: { label: 'Process', enabled: true, url: '#process' },
    testimonials: { label: 'Testimonials', enabled: true, url: '#testimonials' },
    blogs: { label: 'Blogs', enabled: false, url: '/blogs' },
    contact: { label: 'Contact', enabled: true, url: '#contact' }
  });
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [contactInfo, setContactInfo] = useState({
    phone: '+91 98765 43210',
    email: 'info@visaasure.com',
    address: 'Office 301, Raheja Chambers, Nariman Point, Mumbai - 400021, India',
    whatsapp: '+91 98765 43210',
    hours: 'Mon-Sat: 10:00 AM - 7:00 PM'
  });

  useEffect(() => {
    fetchFooterContent();
    fetchQuickLinks();
    fetchContactInfo();
    fetchRecentBlogs();
  }, []);

  const fetchFooterContent = async () => {
    try {
      const response = await axios.get(`${API}/settings/footer_content`);
      setFooterContent(response.data.setting_value);
    } catch (error) {
      console.error('Failed to fetch footer content:', error);
    }
  };

  const fetchQuickLinks = async () => {
    try {
      const response = await axios.get(`${API}/settings/quick_links`);
      setQuickLinks(response.data.setting_value);
    } catch (error) {
      console.error('Failed to fetch quick links:', error);
    }
  };

  const fetchRecentBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs/published?limit=4`);
      setRecentBlogs(response.data);
    } catch (error) {
      console.error('Failed to fetch recent blogs:', error);
    }
  };

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get(`${API}/settings/contact_info`);
      setContactInfo(response.data.setting_value);
    } catch (error) {
      console.error('Failed to fetch contact info:', error);
    }
  };

  const handleQuickLinkClick = (e, url) => {
    if (url.includes('#contact')) {
      e.preventDefault();
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Focus on full name field after scroll
        setTimeout(() => {
          const nameInput = document.getElementById('name');
          if (nameInput) {
            nameInput.focus();
          }
        }, 600);
      }
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="heading-3 footer-title">{footerContent.company_name}</h3>
            <p className="body-small footer-description">
              {footerContent.description}
            </p>
          </div>

          <div className="footer-section">
            <h4 className="heading-3 footer-heading">Quick Links</h4>
            <ul className="footer-links">
              {Object.entries(quickLinks)
                .filter(([key, link]) => link.enabled)
                .map(([key, link]) => (
                  <li key={key}>
                    <a 
                      href={link.url} 
                      className="footer-link"
                      onClick={(e) => handleQuickLinkClick(e, link.url)}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              <li>
                <a href="/about-one92" className="footer-link">
                  About One92
                </a>
              </li>
            </ul>
          </div>

          <div className="footer-section">
            <h4 className="heading-3 footer-heading">Recent Blogs</h4>
            <ul className="footer-links">
              {recentBlogs.length === 0 ? (
                <li className="body-small" style={{ color: '#94a3b8' }}>No blogs yet</li>
              ) : (
                recentBlogs.map((blog) => (
                  <li key={blog.id}>
                    <a href={`/blogs/${blog.slug}`} className="footer-link">{blog.title}</a>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="footer-section footer-contact-section">
            <h4 className="heading-3 footer-heading" style={{color: 'white'}}>Contact Us</h4>
            <div className="footer-contact">
              <div className="footer-contact-item">
                <MapPin size={16} color="white" />
                <span className="body-small" style={{color: 'white'}}>{contactInfo.address}</span>
              </div>
              <div className="footer-contact-item">
                <Phone size={16} color="white" />
                <span className="body-small" style={{color: 'white'}}>{contactInfo.phone}</span>
              </div>
              <div className="footer-contact-item">
                <Mail size={16} color="white" />
                <span className="body-small" style={{color: 'white'}}>{contactInfo.email}</span>
              </div>
              <div className="footer-contact-item">
                <Clock size={16} color="white" />
                <span className="body-small" style={{color: 'white'}}>{contactInfo.hours}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="body-small footer-copyright">
            {footerContent.copyright_text}
            <span style={{ margin: '0 8px' }}>•</span>
            <a href="/privacy-policy" className="footer-link" style={{ color: 'inherit', textDecoration: 'underline' }}>
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;